import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { Textstyle } from "@/types/legend.types";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";

/**
 * Local cache of text style properties.
 * ODA may not preserve certain properties (like fileName for TTF fonts),
 * so we cache the values we set to use when reading styles back.
 */

/**
 * Default text style properties.
 */
export const DEFAULT_TEXT_STYLE: Textstyle = {
  name: "DR_DEFAULT",
  filename: "arial.ttf",
  typeface: "Arial",
  size: 2.5,
  bold: false,
  italic: false,
  charset: 0,
};

/**
 * Find a text style by name in the database.
 * @returns The text style ID, or null if not found
 */
export function findTextStyle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  styleName: string,
): DrawingModule.OdDbObjectId | null {
  const textStyleTableId = db.getTextStyleTableId();
  const textStyleTable = openAs(
    textStyleTableId,
    module.OdDbTextStyleTable,
    OpenMode.kForRead,
  );

  const styleNameStr = new module.OdString();
  styleNameStr.set(styleName);

  let styleId: DrawingModule.OdDbObjectId | null = null;

  if (textStyleTable.has1(styleNameStr)) {
    styleId = textStyleTable.getAt3(styleNameStr, false);
  }

  deleteObj(styleNameStr);
  deleteObj(textStyleTable);

  return styleId;
}

/**
 * Create or update a text style in the database.
 * If the style already exists, it will be updated with the new properties.
 * @returns The text style ID
 */
export function ensureTextStyleExists(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  style: Textstyle,
): DrawingModule.OdDbObjectId {
  const textStyleTableId = db.getTextStyleTableId();
  const textStyleTable = openAs(
    textStyleTableId,
    module.OdDbTextStyleTable,
    OpenMode.kForWrite,
  );

  const styleNameStr = new module.OdString();
  styleNameStr.set(style.name);

  let styleId: DrawingModule.OdDbObjectId;

  if (textStyleTable.has1(styleNameStr)) {
    // Style exists - get its ID
    styleId = textStyleTable.getAt3(styleNameStr, false);
    // Update properties on existing style
    setTextStyleProperties(module, styleId, style);
  } else {
    // Create new style
    const newStyle = module.OdDbTextStyleTableRecord.createObject();
    newStyle.setName(styleNameStr);
    styleId = textStyleTable.add(
      newStyle as unknown as DrawingModule.OdDbSymbolTableRecord,
    );

    // Set properties on the new style
    setTextStyleProperties(module, styleId, style);

    deleteObj(newStyle);
  }

  deleteObj(styleNameStr);
  deleteObj(textStyleTable);

  return styleId;
}

/**
 * Set properties on a text style record.
 */
export function setTextStyleProperties(
  module: DrawingModuleType,
  styleId: DrawingModule.OdDbObjectId,
  style: Textstyle,
): void {
  const styleRecord = openAs(
    styleId,
    module.OdDbTextStyleTableRecord,
    OpenMode.kForWrite,
  );

  const isTTF = style.filename?.toLowerCase().endsWith(".ttf");

  // For TTF fonts, ODA uses setFont() with typeface name (registered via ttfFileNameByDescriptor)
  // For SHX fonts, ODA uses setFileName() with the filename
  if (isTTF && style.typeface) {
    // TrueType font: use setFont with typeface name
    const fontStr = new module.OdString();
    fontStr.set(style.typeface.replace(/\.[^.]+$/, ""));
    styleRecord.setFont(
      fontStr,
      style.bold ?? false,
      style.italic ?? false,
      style.charset ?? 0,
      34, // 34 is common for TTF fonts
    );
    deleteObj(fontStr);
  } else if (style.filename) {
    // SHX or other font: use setFileName
    const filenameStr = new module.OdString();
    filenameStr.set(style.filename);
    styleRecord.setFileName(filenameStr);
    deleteObj(filenameStr);
  }

  // Height of the scale: what a text written with this style gets by default,
  // also in a CAD software. Left unset, a new record keeps ODA's default fixed
  // height of 0.2 — every scale then had that same height. Texts, leaders and
  // photo markers set their own height, so a forced size still wins.
  if (style.size !== undefined && style.size > 0) {
    styleRecord.setTextSize(style.size);
  }

  deleteObj(styleRecord);
}

/**
 * Get the "Standard" text style ID (default style in DWG files).
 */
export function getStandardTextStyleId(
  db: DrawingModule.OdDbDatabase,
): DrawingModule.OdDbObjectId {
  return db.getTextStyleStandardId();
}

/**
 * Get all text styles from the database.
 * @returns Array of text style information
 */
export function getAllTextStyles(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): Textstyle[] {
  const styles: Textstyle[] = [];

  const textStyleTableId = db.getTextStyleTableId();
  const textStyleTable = openAs(
    textStyleTableId,
    module.OdDbTextStyleTable,
    OpenMode.kForRead,
  );

  const iterator = textStyleTable.newIterator(true, true);

  while (!iterator.done()) {
    const record = iterator.getRecord(OpenMode.kForRead, false);
    const styleRecord = module.OdDbTextStyleTableRecord.cast(record);

    const nameStr = styleRecord.getName();
    const name = nameStr.toString();
    deleteObj(nameStr);

    // Skip empty or system styles
    if (name && !name.startsWith("*")) {
      const fileNameStr = styleRecord.fileName();
      const fileName = fileNameStr.toString();
      deleteObj(fileNameStr);

      // Get font info
      let font = "";
      let bold = false;
      let italic = false;

      const fontStr = new module.OdString();
      const boldRef = { value: false };
      const italicRef = { value: false };
      const charsetRef = { value: 0 };
      const pitchRef = { value: 0 };

      styleRecord.font(
        fontStr,
        boldRef as any,
        italicRef as any,
        charsetRef as any,
        pitchRef as any,
      );
      font = fontStr.toString();
      bold = boldRef.value;
      italic = italicRef.value;
      deleteObj(fontStr);

      // Get properties - ODA may not preserve some values, so check cache first
      const size = styleRecord.textSize();
      const filenameResult = fileName;
      const fontResult = font;

      styles.push({
        name,
        filename: filenameResult,
        typeface: fontResult,
        size,
        bold,
        italic,
      });
    }

    deleteObj(styleRecord);
    deleteObj(record);
    iterator.step(true, true);
  }

  deleteObj(iterator);
  deleteObj(textStyleTable);

  return styles;
}

/**
 * Apply a text style to a text entity (OdDbText).
 */
export function applyTextStyleToEntity(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  textEntity: DrawingModule.OdDbText,
  style: Textstyle,
): void {
  // Ensure the text style exists
  const styleId = ensureTextStyleExists(module, db, style);

  // Set the text style on the entity
  textEntity.setTextStyle(styleId);

  // Set text height if specified (overrides style default)
  const textHeight = style.size;
  if (textHeight !== undefined && textHeight > 0) {
    textEntity.setHeight(textHeight);
  }
}

/**
 * Apply a text style to an MText entity (OdDbMText).
 */
export function applyTextStyleToMText(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  mtext: DrawingModule.OdDbMText,
  style: Textstyle,
): void {
  // Ensure the text style exists
  const styleId = ensureTextStyleExists(module, db, style);

  // Set the text style on the MText entity
  mtext.setTextStyle(styleId);

  if (style.size) {
    mtext.setTextHeight(style.size);
  }
}

export function applyTextHeightToMText(
  mtext: DrawingModule.OdDbMText,
  height: number,
): void {
  mtext.setTextHeight(height);
}
